/**
 * AutoServiceCard - Card for services with live data from snapshots
 *
 * Extends ServiceCard with refresh button and last-updated timestamp.
 * Firebase card includes billing costs section when available.
 */

import { Typography, Tag } from "antd";
import ServiceCard from "./ServiceCard";
import type { ServiceCardProps } from "./ServiceCard";
import type { ServiceSnapshot, BillingCosts } from "@/services/subscriptionsService";
import { getAlertLevel } from "@/services/subscriptionsService";

const { Text } = Typography;

interface AutoServiceCardProps {
  serviceId: string;
  title: string;
  deepLink: string;
  snapshot: ServiceSnapshot | null;
  extra?: React.ReactNode;
}

function buildFirebaseMetrics(metrics: Record<string, unknown>): ServiceCardProps["metrics"] {
  const monthlyQuotas: Record<string, { label: string; quota: number; unit?: string }> = {
    firestoreReads: { label: "Firestore Reads", quota: 20_000_000 },
    firestoreWrites: { label: "Firestore Writes", quota: 2_000_000 },
    firestoreDeletes: { label: "Firestore Deletes", quota: 500_000 },
    functionsInvocations: { label: "Functions Invocations", quota: 3_000_000 },
    storageBytes: { label: "Storage", quota: 5e9, unit: "bytes" },
    storageBandwidthBytes: { label: "Bandwidth", quota: 50e9, unit: "bytes" },
  };

  return Object.entries(monthlyQuotas).map(([key, config]) => {
    const value = Number(metrics[key] || 0);
    return {
      label: config.label,
      value,
      quota: config.quota,
      unit: config.unit,
      alertLevel: getAlertLevel((value / config.quota) * 100),
    };
  });
}

function buildStripeMetrics(metrics: Record<string, unknown>): ServiceCardProps["metrics"] {
  const mrr = Number(metrics.mrr || 0);
  const currency = String(metrics.currency || "eur").toUpperCase();
  const totalSubs = Number(metrics.totalActiveSubscribers || 0);

  return [
    { label: "MRR", value: `${mrr.toFixed(2)} ${currency}` },
    { label: "Active Subscribers", value: totalSubs },
  ];
}

function buildCloudflareMetrics(metrics: Record<string, unknown>): ServiceCardProps["metrics"] {
  const r2 = metrics.r2 as Record<string, number> | undefined;
  const workers = metrics.workers as Record<string, number> | undefined;
  const pages = metrics.pages as Record<string, number> | undefined;

  return [
    { label: "R2 Storage", value: r2?.storageBytes || 0, unit: "bytes" },
    { label: "Workers Requests", value: workers?.requests || 0 },
    { label: "Workers Errors", value: workers?.errors || 0 },
    { label: "Pages Builds", value: pages?.totalBuilds || 0 },
  ];
}

const metricBuilders: Record<string, (m: Record<string, unknown>) => ServiceCardProps["metrics"]> = {
  firebase: buildFirebaseMetrics,
  stripe: buildStripeMetrics,
  cloudflare: buildCloudflareMetrics,
};

function getOverallAlertLevel(metrics: ServiceCardProps["metrics"]): "ok" | "warning" | "critical" {
  if (!metrics?.length) return "ok";
  if (metrics.some((m) => m.alertLevel === "critical")) return "critical";
  if (metrics.some((m) => m.alertLevel === "warning")) return "warning";
  return "ok";
}

function TrendBadge({ trend }: { trend?: number }) {
  if (trend == null) return null;
  const color = trend > 10 ? "#ff4d4f" : trend < -10 ? "#52c41a" : "#8c8c8c";
  const arrow = trend > 0 ? "↑" : trend < 0 ? "↓" : "";
  return (
    <Text style={{ fontSize: 12, color, marginLeft: 4 }}>
      {arrow}{Math.abs(trend).toFixed(1)}%
    </Text>
  );
}

function currencySymbol(c: string): string {
  if (c === "EUR") return "€";
  if (c === "USD") return "$";
  return c + " ";
}

function BillingSubtitle({ billing }: { billing: BillingCosts }) {
  const sym = currencySymbol(billing.currency);
  return (
    <span style={{ fontSize: 13 }}>
      <Text strong style={{ fontSize: 13 }}>
        {sym}{billing.totalCost.toFixed(2)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {" "}in {billing.daysElapsed} days
      </Text>
      {billing.estimated && (
        <Text type="secondary" style={{ fontSize: 11, fontStyle: "italic" }}>
          {" "}(estimated)
        </Text>
      )}
      {billing.previousMonthTotal != null && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {"  "}(prev: {sym}{billing.previousMonthTotal.toFixed(2)}
          {billing.previousMonthInvoiced ? " invoiced" : ` in ${billing.previousMonthDays} days`})
        </Text>
      )}
      <TrendBadge trend={billing.totalTrend} />
    </span>
  );
}

export default function AutoServiceCard({
  serviceId,
  title,
  deepLink,
  snapshot,
  extra,
}: AutoServiceCardProps) {
  if (!snapshot) {
    return (
      <ServiceCard title={title} alertLevel="ok" deepLink={deepLink} extra={extra}>
        <Text type="secondary">No data yet — run a refresh to fetch metrics.</Text>
      </ServiceCard>
    );
  }

  if (snapshot.status === "error") {
    return (
      <ServiceCard title={title} alertLevel="warning" deepLink={deepLink} extra={extra}>
        <Tag color="error">Error: {snapshot.error || "Unknown error"}</Tag>
        {snapshot.updatedAt && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last attempt: {snapshot.updatedAt.toDate().toLocaleString()}
            </Text>
          </div>
        )}
      </ServiceCard>
    );
  }

  const builder = metricBuilders[serviceId];
  const cardMetrics = builder ? builder(snapshot.metrics) : [];
  const billing = snapshot.billing;
  const alertLevel = getOverallAlertLevel(cardMetrics);

  return (
    <ServiceCard
      title={title}
      subtitle={billing ? <BillingSubtitle billing={billing} /> : undefined}
      alertLevel={alertLevel}
      deepLink={deepLink}
      metrics={cardMetrics}
      extra={extra}
    >
      {snapshot.updatedAt && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Last updated: {snapshot.updatedAt.toDate().toLocaleString()}
        </Text>
      )}
    </ServiceCard>
  );
}
